// BluetoothTest.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { RawBTPrinter } from "../utils/rawbtPrinter";
import ReceiptPreview from "./ReceiptPreview";
import { SAVED_URL } from "../components/api/apiurls";
import { AuthContext } from "../components/ContextApi/ContextApi";
import Navbar from "../components/shared/Navbar";
// Utility: get latest entry per account
const getLatestEntries = (data = []) => {
  const latestMap = {};
  data.forEach((entry) => {
    if (!entry) return;
    const accNo = String(entry.AccountNo);
    if (!latestMap[accNo] || entry.id > latestMap[accNo].id) {
      latestMap[accNo] = entry;
    }
  });
  return Object.values(latestMap);
};

export default function BluetoothTest() {
  const { state } = useLocation(); // state from navigation
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ useContext for bank + agentUserName
  const { firstName, lastName, bank, userName: agentUserName, userMobile } = useContext(AuthContext);

  // helpers
  const padLine = (label, value, lineWidth = 32) => {
    const labelStr = label || "";
    const valueStr = value || "";
    let spaces = lineWidth - labelStr.length - valueStr.length;
    if (spaces < 1) spaces = 1;
    return labelStr + " ".repeat(spaces) + valueStr;
  };

  const formatAmount = (val) => {
    if (val === undefined || val === null || val === "") return "0.00";
    let num = parseFloat(val);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  // Fetch API once component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${SAVED_URL}${agentUserName}/`); // ✅ fixed
        const data = await res.json();
        setApiData(data);
      } catch (err) {
        console.error("API fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [agentUserName]);

  // Pick the latest entry for the requested account number
  const entry = useMemo(() => {
    if (!apiData.length) return null;
    const latest = getLatestEntries(apiData);
    if (state?.AccountNo) {
      return (
        latest.find((e) => String(e.AccountNo) === String(state.AccountNo)) ||
        null
      );
    }
    return latest[0] || null;
  }, [apiData, state?.AccountNo]);

  // Build preview text
  const previewText = useMemo(() => {
    if (!entry) return "No receipt data available.";
    let lines = [];
    lines.push("  **** RECEIPT ****");
    lines.push(` ${bank || ""}`); // ✅ use bank from context
    lines.push(
      `Rec No: ${entry.id || "----"}   Date: ${
        entry.Today || new Date().toLocaleDateString()
      }`
    );
    lines.push(`NAME : ${entry.Name || ""}`);
    lines.push(`ACCOUNT NO : ${entry.AccountNo || ""}`);
    lines.push(`ACCOUNT TYPE: ${entry.AccountType || ""}`);
    lines.push("");
    lines.push(padLine("OPENING BALANCE", formatAmount(entry.PreviousBalance)));
    lines.push(padLine("RECEIVED", formatAmount(entry.Recieved)));
    lines.push(padLine("BALANCE", formatAmount(entry.PresentBalance)));
    lines.push("");
    lines.push("  THANK YOU");
    lines.push(`       ${firstName + " " + lastName || ""}`); // ✅ use firstName and lastName from context
    lines.push(`  ${userMobile || ""}`);
    return lines.join("\n");
  }, [entry, bank, firstName,lastName,userMobile]);

  // const handlePrintReceipt = () => {
  //   if (!entry) {
  //     alert("No receipt data found!");
  //     return;
  //   }

  //   let commands = "";
  //   commands += String.fromCharCode(27) + "@"; // Initialize printer
  //   commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);
  //   commands += "**** RECEIPT ****\n";
  //   commands += ` ${bank || ""}\n`; // ✅ corrected

  //   commands += String.fromCharCode(27) + "a" + String.fromCharCode(0);
  //   commands += `Rec No: ${entry.id || "----"}    Date: ${
  //     entry.Today || new Date().toLocaleDateString()
  //   }\n`;
  //   commands += `NAME : ${entry.Name || ""}\n`;
  //   commands += `ACCOUNT NO : ${entry.AccountNo || ""}\n`;
  //   commands += `ACCOUNT TYPE: ${entry.AccountType || ""}\n\n`;

  //   commands +=
  //     padLine("OPENING BALANCE", formatAmount(entry.PreviousBalance)) + "\n";
  //   commands += padLine("RECEIVED", formatAmount(entry.Recieved)) + "\n";
  //   commands += padLine("BALANCE", formatAmount(entry.PresentBalance)) + "\n";

  //   commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);
  //   commands += "THANK YOU\n";
  //   commands += `${firstName + " " + lastName || ""}\n`; // ✅ corrected
  //   commands += `${userMobile || ""}\n\n\n`;

  //   RawBTPrinter.printEscPos(commands);
  // };
const handlePrintReceipt = () => {
  if (!entry) {
    alert("No receipt data found!");
    return;
  }

  let commands = "";
  commands += String.fromCharCode(27) + "@"; // Initialize printer

  // ✅ Center align
  commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);

  // ✅ Set Double Width (normal height) + Bold for the title
  commands += String.fromCharCode(27) + "!" + String.fromCharCode(32); // ESC ! 32 (Double width)
  commands += String.fromCharCode(27) + "E" + String.fromCharCode(1); // Bold ON
  commands += "*** RECEIPT ***\n";
  commands += String.fromCharCode(27) + "E" + String.fromCharCode(0); // Bold OFF

  // ✅ Reset to normal font size
  commands += String.fromCharCode(27) + "!" + String.fromCharCode(0);

  // ✅ Bold ON for bank name
  commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
  commands += `${bank || ""}\n`;
  commands += String.fromCharCode(27) + "E" + String.fromCharCode(0); // Bold OFF

  // ✅ Left align
  commands += String.fromCharCode(27) + "a" + String.fromCharCode(0);

  // ✅ Transaction details
  commands += `Rec No: ${entry.id || "----"}  Date: ${entry.Today || new Date().toLocaleDateString()}\n`;
  commands += `NAME : ${entry.Name || ""}\n`;
  commands += `ACCOUNT NO : ${entry.AccountNo || ""}\n`;
  commands += `ACCOUNT TYPE: ${entry.AccountType || ""}\n\n`;

  // ✅ Bold ON for balances
  commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
  commands += padLine("OPENING BALANCE", formatAmount(entry.PreviousBalance)) + "\n";
  commands += padLine("RECEIVED", formatAmount(entry.Recieved)) + "\n";
  commands += padLine("BALANCE", formatAmount(entry.PresentBalance)) + "\n";
  commands += String.fromCharCode(27) + "E" + String.fromCharCode(0); // Bold OFF

  // ✅ Spacing
  commands += "\n";

  // ✅ Center align + THANK YOU in double width only (normal height)
  commands += String.fromCharCode(27) + "a" + String.fromCharCode(1); // Center
  commands += String.fromCharCode(27) + "!" + String.fromCharCode(32); // ESC ! 32 (Double width)
  commands += "THANK YOU\n";

  // ✅ Reset font to normal
  commands += String.fromCharCode(27) + "!" + String.fromCharCode(0);

  // ✅ Agent name and mobile
  commands += `${firstName + " " + lastName || ""}\n`;
  commands += `${userMobile || ""}\n`;

  // ✅ Optional: feed lines for cutting
  // commands += "\n\n\n";

  // ✅ Send to printer
  RawBTPrinter.printEscPos(commands);
};


  return (
    <div>
      <Navbar />
    <div style={{ padding: 20, textAlign: "center", marginTop: "20px" }}>
      {/* <h2>Bluetooth Printer Test</h2> */}

      {loading ? (
        <p>Loading...</p>
      ) : entry ? (
        <>
          <ReceiptPreview text={previewText} />
          <button
            onClick={handlePrintReceipt}
            style={{ marginTop: 20, padding: "10px" }}
          >
            🖨 Print Receipt
          </button>
        </>
      ) : (
        <p>No matching account found</p>
      )}
    </div>
    </div>
  );
}
